import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { LOGO_DARK } from "@/lib/brand";
import { useReveal } from "@/hooks/use-reveal";
import { ArrowLeft, CalendarDays, ArrowUpRight } from "lucide-react";
import PublicNav from "@/components/public/PublicNav";
import { BlogPostSkeleton } from "@/components/public/Skeletons";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [others, setOthers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const revealRef = useReveal(!!post);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    (async () => {
      try {
        const [p, all, s] = await Promise.all([
          api.get(`/content/blog/${slug}`),
          api.get("/content/blog"),
          api.get("/content/settings"),
        ]);
        setPost(p.data);
        setOthers((all.data || []).filter((x) => x.slug !== slug && x.enabled !== false).slice(0, 2));
        setSettings(s.data);
      } catch {
        setNotFound(true);
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (post?.title) document.title = `${post.title} · Aiyavi Retreat`;
  }, [post]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-earth-700 gap-4 px-6 text-center">
        <div className="font-serif text-3xl text-earth-900">Post not found</div>
        <p className="text-earth-500">This story may have moved or been removed.</p>
        <button
          onClick={() => navigate("/blog")}
          data-testid="blog-not-found-back"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-earth-900 text-white text-sm hover:bg-[#FF6B35] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to journal
        </button>
      </div>
    );
  }

  if (!post) {
    return <BlogPostSkeleton />;
  }

  return (
    <div ref={revealRef} data-testid="blog-post" className="bg-earth-50 min-h-screen">
      <PublicNav settings={settings} sectionPrefix="/" />

      {/* Hero with cover */}
      <header className="relative h-[60vh] sm:h-[70vh] min-h-[480px] overflow-hidden text-white">
        {post.cover_url ? (
          <img
            src={post.cover_url}
            alt={post.title}
            className="absolute inset-0 w-full h-full object-cover animate-ken-burns"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-earth-700 to-earth-900" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-earth-900/40 via-earth-900/50 to-earth-900/85" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 pt-32 sm:pt-40">
          <div className="reveal">
            <div className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-[#FF6B35]">
              <Link
                to="/blog"
                className="inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                data-testid="post-back-blog"
              >
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Journal
              </Link>
              <span className="w-6 h-px bg-[#FF6B35]" />
              <span className="text-white/70 flex items-center gap-1.5">
                <CalendarDays className="w-3.5 h-3.5" strokeWidth={1.5} />
                {formatDate(post.date)}
              </span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light mt-4 leading-[1.05] tracking-tight">
              {post.title}
            </h1>
            {post.excerpt && (
              <p className="mt-5 max-w-2xl text-white/85 font-light text-base sm:text-lg leading-relaxed">
                {post.excerpt}
              </p>
            )}
            <div className="mt-6 text-sm text-white/70">By {post.author || "Aiyavi Team"}</div>
          </div>
        </div>
      </header>

      {/* Body */}
      <article className="px-6 py-14 sm:py-20">
        <div className="max-w-3xl mx-auto reveal">
          <div
            data-testid="post-body"
            className="blog-content font-light text-earth-800 text-base sm:text-lg leading-[1.8]"
            dangerouslySetInnerHTML={{ __html: post.content || "" }}
          />
        </div>
      </article>

      {/* Related */}
      {others.length > 0 && (
        <section className="px-6 pb-16 bg-beige/30 pt-12">
          <div className="max-w-7xl mx-auto">
            <div className="reveal flex items-baseline justify-between mb-7">
              <h3 className="font-serif text-3xl sm:text-4xl text-earth-900 font-light">More from the journal</h3>
              <Link to="/blog" className="text-sm text-[#FF6B35] inline-flex items-center gap-1.5" data-testid="post-all-link">
                All stories <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 gap-6 stagger">
              {others.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  data-testid={`related-${p.slug}`}
                  className="reveal group bg-white rounded-3xl overflow-hidden border border-earth-100 hover:shadow-[0_24px_50px_-25px_rgba(60,40,20,0.3)] transition-shadow duration-500 flex flex-col"
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    {p.cover_url ? (
                      <img src={p.cover_url} alt={p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-earth-200 to-earth-400" />
                    )}
                  </div>
                  <div className="p-6 flex flex-col gap-2">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-earth-500 flex items-center gap-1.5">
                      <CalendarDays className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {formatDate(p.date)}
                    </div>
                    <h4 className="font-serif text-2xl text-earth-900 leading-tight group-hover:text-[#FF6B35] transition-colors">{p.title}</h4>
                    {p.excerpt && <p className="text-earth-600 text-sm leading-relaxed line-clamp-2">{p.excerpt}</p>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="px-6 pb-10 pt-6 bg-beige/40 border-t border-earth-100">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4 text-sm text-earth-600">
          <img src={LOGO_DARK} alt="Aiyavi Retreat" className="h-10 w-auto object-contain" />
          <div className="flex items-center gap-5">
            <Link to="/" className="hover:text-earth-900">Home</Link>
            <Link to="/blog" className="hover:text-earth-900">Blog</Link>
            <a href="/admin/login" className="hover:text-earth-900">Admin</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
